import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Image, Select, Space, Typography, Upload, message } from 'antd';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import {
  deleteReferenceImage,
  listReferenceImages,
  uploadReferenceImage,
  type ReferenceImage,
} from '@/services/recognition.service';

const VIEW_TYPES = ['front', 'side', 'label', 'packaging', 'shelf', 'other'];

type Props = {
  itemId: string;
  /** View-only: hide upload / delete controls. */
  readOnly?: boolean;
};

export const ProductGalleryPanel: React.FC<Props> = ({ itemId, readOnly = false }) => {
  const [images, setImages] = useState<ReferenceImage[]>([]);
  const [viewType, setViewType] = useState('front');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    try {
      const data = await listReferenceImages(itemId);
      setImages(data);
    } catch {
      message.error('Failed to load reference images');
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onUpload = async (file: File) => {
    try {
      await uploadReferenceImage(itemId, file, viewType, images.length === 0);
      message.success('Image uploaded');
      await load();
    } catch {
      message.error('Upload failed');
    }
    return false;
  };

  const onDelete = async (imageId: string) => {
    try {
      await deleteReferenceImage(itemId, imageId);
      message.success('Image removed');
      await load();
    } catch {
      message.error('Delete failed');
    }
  };

  return (
    <Card
      title={readOnly ? undefined : 'Recognition gallery'}
      size="small"
      loading={loading}
      style={readOnly ? undefined : { marginTop: 16 }}
      bordered={!readOnly}
    >
      {!readOnly && (
        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Upload reference images (front, label, packaging) to improve vision matching.
        </Typography.Text>
      )}
      {!readOnly && (
        <Space wrap style={{ marginBottom: 12 }}>
          <Select
            value={viewType}
            onChange={setViewType}
            style={{ width: 140 }}
            options={VIEW_TYPES.map((v) => ({ value: v, label: v }))}
          />
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={(f) => {
              void onUpload(f);
              return false;
            }}
          >
            <Button icon={<UploadOutlined />}>Add image</Button>
          </Upload>
        </Space>
      )}
      <Space wrap>
        {images.length === 0 && readOnly ? (
          <Typography.Text type="secondary">No images</Typography.Text>
        ) : null}
        {images.map((img) => (
          <Card
            key={img.id}
            size="small"
            style={{ width: 140 }}
            cover={
              <Image
                src={img.imageUrl}
                alt={img.viewType}
                height={100}
                style={{ objectFit: 'cover' }}
              />
            }
            actions={
              readOnly
                ? undefined
                : [
                    <Button
                      key="del"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => void onDelete(img.id)}
                    />,
                  ]
            }
          >
            <Typography.Text ellipsis>
              {img.viewType}
              {img.isPrimary ? ' (primary)' : ''}
            </Typography.Text>
          </Card>
        ))}
      </Space>
    </Card>
  );
};
